import routes from '../../seo/routes.json';
import { toolPaths } from '../components/Navigation/toolNavItems';

export type PageType =
  | 'home'
  | 'tool'
  | 'news_index'
  | 'news_topic'
  | 'article'
  | 'resources'
  | 'about'
  | 'legal'
  | 'coming_soon'
  | 'dev'
  | 'other';

export interface PageMeta {
  title: string;
  pageType: PageType;
  toolId?: string;
}

interface SeoRoute {
  path: string;
  title: string;
}

// seo/routes.json is the single source of route titles -- it already drives
// scripts/prerender.mjs and the sitemap, so titles are never duplicated here.
const titleByPath = new Map((routes as SeoRoute[]).map((r) => [r.path, r.title]));

// /trade-trends is a real tool page but deliberately outside the toolNavItems
// registry (it sits in its own top-level nav slot), so it's added explicitly.
const TOOL_PATHS = new Set([...toolPaths, '/trade-trends']);

const FALLBACK_TITLE = 'Global Cart Kit';

function titleFromSlug(slug: string): string {
  const words = slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
  return words ? `${words} - Global Cart Kit` : FALLBACK_TITLE;
}

function classify(pathname: string): { pageType: PageType; toolId?: string } {
  if (pathname === '/') return { pageType: 'home' };
  if (TOOL_PATHS.has(pathname)) {
    return { pageType: 'tool', toolId: pathname.replace(/^\//, '') };
  }
  if (pathname === '/shipping-calculator') return { pageType: 'coming_soon' };
  if (pathname === '/news') return { pageType: 'news_index' };
  if (pathname.startsWith('/news/topic/')) return { pageType: 'news_topic' };
  if (pathname.startsWith('/news/')) return { pageType: 'article' };
  if (pathname === '/resources') return { pageType: 'resources' };
  if (pathname === '/about') return { pageType: 'about' };
  if (pathname === '/privacy' || pathname === '/terms') return { pageType: 'legal' };
  if (pathname.startsWith('/dev/')) return { pageType: 'dev' };
  return { pageType: 'other' };
}

export function resolvePageMeta(pathname: string): PageMeta {
  const { pageType, toolId } = classify(pathname);
  const known = titleByPath.get(pathname);
  if (known) return { title: known, pageType, toolId };

  // Articles and any topic not yet in routes.json publish between deploys, so
  // their titles can't come from the static list.
  const slug = pathname.split('/').filter(Boolean).pop() ?? '';
  if (pageType === 'article' || pageType === 'news_topic') {
    return { title: titleFromSlug(decodeURIComponent(slug)), pageType, toolId };
  }
  return { title: FALLBACK_TITLE, pageType, toolId };
}
